import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  fetchGtmBrain,
  getGtmBrain,
  patchGtmBrain,
  replaceGtmBrainDocs,
  type GtmBrain,
  type GtmBrainDoc,
  type GtmBrainResponse,
  type GtmSiteSourceStatus,
} from "../api/GtmApi";
import { gtmQueryKeys } from "./gtmQueryKeys";

function isBrainBuildInProgress(status?: GtmSiteSourceStatus | null) {
  return status === "gained" || status === "suggesting";
}

export function useGtmBrainQuery(workspaceId: string) {
  return useQuery({
    queryKey: gtmQueryKeys.brain(workspaceId),
    queryFn: () => getGtmBrain(workspaceId),
    enabled: Boolean(workspaceId),
    refetchOnWindowFocus: false,
    staleTime: 10_000,
    refetchInterval: (query) =>
      isBrainBuildInProgress(query.state.data?.siteSource?.status) ? 2000 : false,
  });
}

export function useGtmBrainBuildMutation(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (url: string) => fetchGtmBrain(workspaceId, url),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: gtmQueryKeys.brain(workspaceId) });
    },
  });
}

export function useGtmBrainSaveMutation(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { brain: Partial<GtmBrain>; docs: GtmBrainDoc[] }) => {
      await patchGtmBrain(workspaceId, input.brain);
      return replaceGtmBrainDocs(workspaceId, input.docs);
    },
    onSuccess: (payload: GtmBrainResponse) => {
      queryClient.setQueryData(gtmQueryKeys.brain(workspaceId), payload);
    },
  });
}

export function useGtmBrainPatchMutation(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<GtmBrain>) => patchGtmBrain(workspaceId, body),
    onSuccess: (payload: GtmBrainResponse) => {
      queryClient.setQueryData(gtmQueryKeys.brain(workspaceId), payload);
    },
  });
}
